import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import BackfillDayModal from './BackfillDayModal';
import { formatLocalDate } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shows a dismissible banner the morning after a user skipped logging.
 * Trigger: yesterday has no check-in AND no wellness log AND user has ≥1 active reminder.
 * Dismissal is per-day (localStorage), so it won't reappear until the next missed day.
 */
function getYesterdayStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatLocalDate(d);
}

export default function MissedYesterdayBanner({ getAuthHeader }) {
  const [show, setShow] = useState(false);
  const [yesterday] = useState(getYesterdayStr());
  const [modalOpen, setModalOpen] = useState(false);

  const dismissKey = `missed-yesterday-dismissed-${yesterday}`;

  const checkMissed = useCallback(async () => {
    // Already dismissed today? Skip.
    if (localStorage.getItem(dismissKey)) return;

    try {
      const [checkRes, wellnessRes, remindersRes] = await Promise.all([
        axios.get(`${API}/checkin/day/${yesterday}`, { headers: getAuthHeader() }),
        axios.get(`${API}/wellness/day/${yesterday}`, { headers: getAuthHeader() }),
        axios.get(`${API}/reminders`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);

      const hasActiveReminder = (Array.isArray(remindersRes.data) ? remindersRes.data : [])
        .some(r => r.enabled !== false && !r.is_medication);

      const skippedSupps = !checkRes.data.checked_in
        || !(checkRes.data.supplements_taken && checkRes.data.supplements_taken.length);
      const skippedWellness = !wellnessRes.data.logged;

      if (hasActiveReminder && skippedSupps && skippedWellness) {
        setShow(true);
      }
    } catch (err) {
      // Silently fail — nudge is non-critical.
    }
  }, [yesterday, dismissKey, getAuthHeader]);

  useEffect(() => {
    checkMissed();
  }, [checkMissed]);

  const dismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setShow(false);
  };

  const niceDate = (() => {
    const [y, m, d] = yesterday.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'long', month: 'short', day: 'numeric',
    });
  })();

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="overflow-hidden"
            data-testid="missed-yesterday-banner"
          >
            <div className="relative bg-gradient-to-r from-violet-50 via-fuchsia-50 to-amber-50 border border-violet-200/70 rounded-2xl p-4 mb-4 shadow-sm">
              <button
                onClick={dismiss}
                className="absolute top-2 right-2 w-7 h-7 rounded-lg hover:bg-white/60 flex items-center justify-center text-violet-700/60 hover:text-violet-900 transition-colors"
                aria-label="Dismiss"
                data-testid="missed-yesterday-dismiss"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex items-start gap-3 pr-6">
                <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm text-[#1A1A1A] mb-0.5">
                    Missed {niceDate}?
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                    Life happens. Backfill yesterday in 30 seconds so your streak stays intact.
                  </p>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 transition-colors"
                    data-testid="missed-yesterday-backfill-btn"
                  >
                    Backfill yesterday
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <BackfillDayModal
        open={modalOpen}
        date={yesterday}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          // Once user saves anything for yesterday, hide the nudge for today.
          localStorage.setItem(dismissKey, '1');
          setShow(false);
        }}
        getAuthHeader={getAuthHeader}
      />
    </>
  );
}
